<script setup>
import { i18n } from 'boot/i18n';
import { useDialogPluginComponent } from 'quasar';
import {
  HrCheckbox,
  HrConfirmBtn,
  HrDataTable,
  HrDismissBtn,
  HrFormDialog,
  HrTd,
  HrTh,
} from 'src/design-system';
import { computed, onMounted, ref } from 'vue';
import {
  useCohortSuggestedUpdatesAnalytics,
} from 'src/domains/careModel/ui/composables/useCohortSuggestedUpdatesAnalytics';

defineOptions({ name: 'SuggestedCohortUpdatesDialog' });

const props = defineProps({
  rows: {
    type: Array,
    required: true,
  },
  cohorts: {
    type: Array,
    required: true,
  },
});

defineEmits([...useDialogPluginComponent.emits]);

const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();

const {
  trackSuggestedUpdatesModalShown,
  trackSuggestedUpdatesModalCancelled,
  trackSuggestedUpdatesModalConfirmed,
} = useCohortSuggestedUpdatesAnalytics();

const pagination = ref({
  rowsPerPage: 10,
});

const localRows = ref(
  props.rows.map((row) => ({
    ...row,
    selected: row.selected ?? true,
  })),
);

const cohortMap = computed(() => {
  return props.cohorts.reduce((acc, cohort) => {
    acc[cohort.id] = cohort.cohortName;
    return acc;
  }, {});
});

const allSelected = computed(() => {
  return localRows.value.length > 0 && localRows.value.every((row) => row.selected);
});

const someSelected = computed(() => {
  return localRows.value.some((row) => row.selected);
});

const selectedRows = computed(() => {
  return localRows.value.filter((row) => row.selected);
});

const canUpdate = computed(() => {
  return selectedRows.value.length > 0;
});

const columns = [
  {
    name: 'selected',
    label: '',
    align: 'left',
    field: 'selected',
  },
  {
    name: 'client',
    label: i18n.t('settings.careModel.cohorts.review.client'),
    align: 'left',
    field: (row) => row.client?.clientName || '',
  },
  {
    name: 'currentCohort',
    label: i18n.t('settings.careModel.cohorts.suggestedUpdates.currentCohort'),
    align: 'left',
    field: 'currentCohortId',
  },
  {
    name: 'suggestedCohort',
    label: i18n.t('settings.careModel.cohorts.review.suggestedCohort'),
    align: 'left',
    field: (row) => row.suggestedCohort?.cohortName || '',
  },
  {
    name: 'matches',
    label: i18n.t('settings.careModel.cohorts.review.parameterMatches'),
    align: 'left',
    field: (row) => row.matchedLabels || [],
    format: (value) => value.join(', '),
  },
];

function toggleAll(value) {
  localRows.value = localRows.value.map((row) => ({
    ...row,
    selected: !!value,
  }));
}

function toggleRow(clientId, value) {
  localRows.value = localRows.value.map((row) => {
    if (row.client.id !== clientId) {
      return row;
    }

    return {
      ...row,
      selected: !!value,
    };
  });
}

function getCurrentCohortName(currentCohortId) {
  if (!currentCohortId) {
    return '—';
  }

  return cohortMap.value[currentCohortId] || '—';
}

function onCancel() {
  trackSuggestedUpdatesModalCancelled(localRows.value.length);
  onDialogCancel();
}

function onUpdate() {
  if (!canUpdate.value) {
    return;
  }

  trackSuggestedUpdatesModalConfirmed(selectedRows.value.length);
  onDialogOK(selectedRows.value);
}

onMounted(() => {
  trackSuggestedUpdatesModalShown(localRows.value.length);
});
</script>

<template>
  <hr-form-dialog
    ref="dialogRef"
    size="lg"
    :title="i18n.t('settings.careModel.cohorts.suggestedUpdates.title')"
    @hide="onDialogHide"
    @close="onCancel"
  >
    <div class="row q-col-gutter-md">
      <p class="col-12 q-ma-none">
        {{
          i18n.t('settings.careModel.cohorts.suggestedUpdates.description', {
            count: localRows.length,
          })
        }}
      </p>
      <div class="col-12">
        <hr-data-table
          :rows="localRows"
          :columns="columns"
          row-key="client.id"
          :pagination="pagination"
        >
          <template #header-cell-selected="headerProps">
            <hr-th :props="headerProps">
              <hr-checkbox
                :model-value="allSelected"
                :indeterminate="someSelected && !allSelected"
                :aria-label="i18n.t('settings.careModel.cohorts.suggestedUpdates.selectAllClients')"
                @update:model-value="toggleAll"
              />
            </hr-th>
          </template>

          <template #body-cell-selected="cellProps">
            <hr-td :props="cellProps">
              <hr-checkbox
                :model-value="cellProps.row.selected"
                :aria-label="i18n.t('settings.careModel.cohorts.suggestedUpdates.selectClient', {
                  clientName: cellProps.row.client?.clientName,
                })"
                @update:model-value="toggleRow(cellProps.row.client.id, $event)"
              />
            </hr-td>
          </template>

          <template #body-cell-client="cellProps">
            <hr-td :props="cellProps">
              {{ cellProps.row.client?.clientName }}
            </hr-td>
          </template>

          <template #body-cell-currentCohort="cellProps">
            <hr-td :props="cellProps">
              {{ getCurrentCohortName(cellProps.row.currentCohortId) }}
            </hr-td>
          </template>

          <template #body-cell-suggestedCohort="cellProps">
            <hr-td :props="cellProps">
              {{ cellProps.row.suggestedCohort?.cohortName }}
            </hr-td>
          </template>

          <template #body-cell-matches="cellProps">
            <hr-td :props="cellProps">
              {{ (cellProps.row.matchedLabels || []).join(', ') }}
            </hr-td>
          </template>
        </hr-data-table>
      </div>
    </div>
    <template #actions>
      <hr-dismiss-btn
        :label="i18n.t('common.buttonSkip')"
        @click="onCancel"
      />
      <hr-confirm-btn
        :label="i18n.t('common.buttonUpdate')"
        :disable="!canUpdate"
        @click="onUpdate"
      />
    </template>
  </hr-form-dialog>
</template>
